import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import { Capacitor } from '@capacitor/core';
import './App.css';
import { Toaster } from './components/ui/sonner';
import { toast } from 'sonner';
import { LanguageProvider } from './i18n/LanguageContext';

// Import des composants
import Login from './components/Login';
import Register from './components/Register';
import Dashboard from './components/Dashboard';
import ProDashboard from './components/ProDashboard';
import ParticulierDashboard from './components/ParticulierDashboard';
import ResetPassword from './components/ResetPassword';
import PrivacyPolicy from './components/PrivacyPolicy';
import DeleteAccountPage from './components/DeleteAccountPage';
import DashboardErrorBoundary from './components/DashboardErrorBoundary';
import AdManager from './components/AdManager';
import { configureRuntimeLogger } from './services/runtimeLogger';
import { flushQueuedAnalytics } from './services/analyticsService';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const DEFAULT_BACKEND_ORIGIN = 'https://api.smartinventory.fr';
const BUILD_STAMP = '2026-06-30-login-hotfix-2';

const isLocalBackend = (url) => {
  if (!url) return true;
  return /localhost|127\.0\.0\.1/i.test(String(url));
};

const isNativeCapacitorRuntime = () => {
  if (typeof window === 'undefined') return false;
  if (window.location?.protocol === 'capacitor:' || window.location?.protocol === 'ionic:') return true;
  if (typeof Capacitor?.isNativePlatform === 'function' && Capacitor.isNativePlatform()) return true;
  if (typeof Capacitor?.getPlatform === 'function' && Capacitor.getPlatform() !== 'web') return true;
  const cap = window.Capacitor;
  return Boolean(cap && typeof cap.isNativePlatform === 'function' && cap.isNativePlatform());
};

// In production builds, fallback to same-origin API when env points to localhost
// to avoid broken auth calls from mobile/public clients.
const resolvedBackendOrigin = (() => {
  if (BACKEND_URL && !isLocalBackend(BACKEND_URL)) {
    return BACKEND_URL.replace(/\/+$/, '');
  }

  if (isNativeCapacitorRuntime()) {
    return DEFAULT_BACKEND_ORIGIN;
  }

  return window.location.origin;
})();

// Configuration axios - always use /api prefix.
axios.defaults.baseURL = `${resolvedBackendOrigin}/api`;

// Context d'authentification
export const AuthContext = React.createContext();

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));

  // Configuration de l'intercepteur axios pour inclure le token
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [token]);

  useEffect(() => {
    console.info(`[App] Build ${BUILD_STAMP}`);
  }, []);

  useEffect(() => {
    try {
      configureRuntimeLogger(() => ({
        user_id: user?.id || null,
        username: user?.username || null,
        user_type: user?.user_type || null,
        has_token: Boolean(token),
      }));
      console.log('[App] Runtime logger configured');
    } catch (err) {
      console.error('[App] Failed to configure runtime logger:', err);
    }
    flushQueuedAnalytics();
  }, [user, token]);

  // Vérifier l'authentification au démarrage
  useEffect(() => {
    const checkAuth = async () => {
      console.log('[App] Starting auth check...');
      if (token) {
        try {
          console.log('[App] Fetching user data...');
          const response = await axios.get('/auth/me');
          console.log('[App] User data received:', response.data);
          setUser(response.data);
        } catch (error) {
          console.error('[App] Auth error:', error.message);
          localStorage.removeItem('token');
          setToken(null);
        }
      } else {
        console.log('[App] No token found, logged out state');
      }
      console.log('[App] Loading complete, setting loading=false');
      setLoading(false);
    };

    checkAuth();
  }, [token]);

  const login = async (username, password) => {
    try {
      const response = await axios.post('/auth/login', { username, password });
      const { access_token, user: userData } = response.data;
      
      localStorage.setItem('token', access_token);
      setToken(access_token);
      setUser(userData);
      
      if (userData.is_temporary_password || userData.must_change_password) {
        toast.warning('Vous êtes connecté avec un mot de passe temporaire. Veuillez le changer immédiatement.');
      } else {
        toast.success('Connexion réussie!');
      }
      
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur de connexion');
      return false;
    }
  };

  const register = async (userData) => {
    try {
      const response = await axios.post('/auth/register', userData);
      const { access_token, user: newUser } = response.data;
      
      localStorage.setItem('token', access_token);
      setToken(access_token);
      setUser(newUser);
      
      toast.success('Inscription réussie!');
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur d\'inscription');
      return false;
    }
  };

  const logout = (message) => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);

    const logoutMessage = typeof message === 'string' ? message : null;
    if (logoutMessage) {
      toast.error(logoutMessage);
    } else {
      toast.success('Deconnexion reussie');
    }
  };

  // Axios interceptor: auto-logout on 401
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      response => response,
      error => {
        if (error.response?.status === 401 && token) {
          localStorage.removeItem('token');
          setToken(null);
          setUser(null);
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(interceptor);
  }, [token]);

  const refreshUser = async () => {
    try {
      const response = await axios.get('/auth/me');
      setUser(response.data);
    } catch (error) {
      console.error('[App] refreshUser error:', error.message);
    }
  };

  const authContextValue = {
    user,
    login,
    register,
    logout,
    refreshUser,
    isAuthenticated: !!user
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-50 via-cyan-50 to-teal-100">
        <div className="flex flex-col items-center space-y-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
          <p className="text-emerald-700 font-medium">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <LanguageProvider>
    <AuthContext.Provider value={authContextValue}>
      <BrowserRouter>
        <div className="App">
          <Routes>
            <Route path="/login" element={
              !user ? <Login /> : <Navigate to="/dashboard" replace />
            } />
            <Route path="/register" element={
              !user ? <Register /> : <Navigate to="/dashboard" replace />
            } />
            <Route path="/reset-password" element={
              <ResetPassword />
            } />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/delete-account" element={<DeleteAccountPage />} />
            <Route path="/dashboard" element={
              user ? (
                <DashboardErrorBoundary onLogout={() => logout('Session reinitialisee apres une erreur interface.')} boundaryName="dashboard-route">
                  {user.user_type === 'PRO' ? <ProDashboard /> : <ParticulierDashboard />}
                </DashboardErrorBoundary>
              ) : <Navigate to="/login" replace />
            } />
            <Route path="/" element={
              user ? <Navigate to="/dashboard" replace /> : <Navigate to="/login" replace />
            } />
          </Routes>
          <AdManager isAuthenticated={Boolean(user)} user={user} />
          <Toaster position="top-right" richColors />
        </div>
      </BrowserRouter>
    </AuthContext.Provider>
    </LanguageProvider>
  );
}

export default App;